import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { observable, flow, action, runInAction } from "mobx";
import { BaseOptions } from "@devowl-wp/utils";
import { request } from "../utils";
import { locationRestRevisionGet, locationRestRevisionIndependentGet, locationRestConsentAllGet, locationRestConsentAllDelete, locationRestConsentRefererGet } from "../wp-api";
import { Consent, Revision, RevisionIndependent } from "../models";
const DATE_FORMAT = "YYYY-MM-DD";
class ConsentStore extends BaseOptions {
    constructor(rootStore){
        super();
        this.busyConsent = false;
        this.busyReferer = false;
        this.count = 0;
        this.truncatedIpsCount = 0;
        this.perPage = 50;
        this.offset = 0;
        /**
     * Current visible page in Consent tab
     */ this.pageCollection = new Map();
        this.revisions = new Map();
        this.revisionsIndependent = new Map();
        this.referer = [];
        this.filters = observable.object({
            page: 1,
            dates: [
                undefined,
                undefined
            ],
            context: undefined,
            referer: undefined,
            ip: undefined,
            uuid: undefined
        }, {}, {
            deep: false
        });
        this.fetchAll = flow(function*() {
            this.busyConsent = true;
            try {
                const { page, referer, ip, uuid, context } = this.filters;
                const dates = this.filters.dates.map((m)=>m ? m.format(DATE_FORMAT) : "");
                const { count, truncatedIpsCount, items } = yield request({
                    location: locationRestConsentAllGet,
                    params: {
                        per_page: this.perPage,
                        offset: (page - 1) * this.perPage,
                        from: dates[0],
                        to: dates[1],
                        ip,
                        uuid,
                        referer,
                        context
                    }
                });
                this.count = count;
                if (truncatedIpsCount) {
                    this.truncatedIpsCount = truncatedIpsCount;
                }
                this.pageCollection.clear();
                for (const item of items){
                    this.pageCollection.set(item.id, new Consent(item, this));
                }
            } catch (e) {
                this.count = 0;
                this.truncatedIpsCount = 0;
                this.pageCollection.clear();
                console.log(e);
                throw e;
            } finally{
                this.busyConsent = false;
            }
        });
        this.fetchRevision = flow(function*(params) {
            try {
                const response = yield request({
                    location: locationRestRevisionGet,
                    params
                });
                this.revisions.set(params.hash, new Revision(response, this));
            } catch (e) {
                console.log(e);
                throw e;
            }
        });
        this.fetchRevisionIndependent = flow(function*(params) {
            try {
                const response = yield request({
                    location: locationRestRevisionIndependentGet,
                    params
                });
                this.revisionsIndependent.set(params.hash, new RevisionIndependent(response, this));
            } catch (e) {
                console.log(e);
                throw e;
            }
        });
        this.fetchReferer = flow(function*(params) {
            this.busyReferer = true;
            try {
                const response = yield request({
                    location: locationRestConsentRefererGet,
                    params
                });
                this.referer = response.items;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyReferer = false;
            }
        });
        this.deleteAll = flow(function*() {
            this.busyConsent = true;
            try {
                yield request({
                    location: locationRestConsentAllDelete
                });
                this.applyPage(0);
                yield this.fetchAll();
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyConsent = false;
            }
        });
        this.rootStore = rootStore;
        runInAction(()=>{
            this.filters.context = this.rootStore.optionStore.others.context;
        });
    }
    /**
     * This action does not actually refetch the list, you need to act on your components!
     */ applyPage(page) {
        this.filters.page = page;
    }
    /**
     * This action does not actually refetch the stats, you need to act on your components!
     *
     * @param dates
     */ applyDates(dates) {
        this.filters.dates = dates;
    }
    /**
     * This action does not actually refetch the stats, you need to act on your components!
     *
     * @param context
     */ applyContext(context) {
        this.filters.context = context;
    }
    /**
     * This action does not actually refetch the list, you need to act on your components!
     */ applyReferer(referer) {
        this.filters.referer = referer;
    }
    /**
     * This action does not actually refetch the list, you need to act on your components!
     */ applyIp(ip) {
        this.filters.ip = ip;
    }
    /**
     * This action does not actually refetch the list, you need to act on your components!
     */ applyUuid(uuid) {
        this.filters.uuid = uuid;
    }
}
_ts_decorate([
    observable
], ConsentStore.prototype, "busyConsent", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "busyReferer", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "count", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "truncatedIpsCount", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "perPage", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "offset", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "pageCollection", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "revisions", void 0);
_ts_decorate([
    observable
], ConsentStore.prototype, "revisionsIndependent", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], ConsentStore.prototype, "referer", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyPage", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyDates", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyContext", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyReferer", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyIp", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], ConsentStore.prototype, "applyUuid", null);
export { ConsentStore };
